import { AuditLogSummary } from "@app/shared";
import { format } from "date-fns";
import { Icons } from "../../../shared";
import JsonViewer from "../../../shared/JsonViewer";

interface AuditLogDetailsProps {
    auditLog: AuditLogSummary;
}

export const AuditLogDetails: React.FC<AuditLogDetailsProps> = ({ auditLog }) => {
    const DetailRow = ({ label, value }: { label: string; value: string | number | React.ReactNode | undefined | null }) => (
        <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
            <span className="font-medium text-gray-700 w-1/3">{label}:</span>
            <div className="flex items-center flex-1">
                <span className="text-gray-900 break-all">{value || "N/A"}</span>
            </div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <Icons.Details className="w-5 h-5 mr-2" />
                        Audit Log Details
                    </h3>
                </div>

                <div className="px-6 py-4 space-y-1">
                    {auditLog.timestamp && <DetailRow label="Timestamp" value={format(new Date(auditLog.timestamp), "PPpp")} />}

                    <DetailRow label="HTTP Method" value={auditLog.httpMethod} />

                    <DetailRow label="Endpoint" value={auditLog.endpoint} />

                    <DetailRow label="Controller" value={auditLog.controllerName} />

                    <DetailRow label="Action" value={auditLog.actionName} />

                    <DetailRow label="User ID" value={auditLog.userId?.toString()} />

                    <DetailRow label="Username" value={auditLog.userName} />

                    <DetailRow
                        label="Response Status"
                        value={
                            <div className="flex items-center">
                                {auditLog.isSuccess ? <Icons.Success className="w-4 h-4 text-green-600 mr-2" /> : <Icons.Close className="w-4 h-4 text-red-600 mr-2" />}
                                <span className={auditLog.isSuccess ? "text-green-600" : "text-red-600"}>
                                    {auditLog.responseStatusCode} {auditLog.isSuccess ? "(Success)" : "(Failed)"}
                                </span>
                            </div>
                        }
                    />

                    <DetailRow label="Execution Time" value={`${auditLog.executionTimeMs} ms`} />

                    <DetailRow label="Trace ID" value={auditLog.traceId} />

                    {auditLog.payload && (
                        <div className="mt-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                                    <Icons.Details className="w-4 h-4 mr-1" />
                                    Request Payload
                                </h4>
                                <div className="max-h-64 overflow-y-auto">
                                    <JsonViewer data={JSON.parse(auditLog.payload)} />
                                </div>
                            </div>
                        </div>
                    )}

                    {auditLog.errorMessage && (
                        <div className="mt-4">
                            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                                <h4 className="text-sm font-medium text-red-800 mb-2 flex items-center">
                                    <Icons.Close className="w-4 h-4 mr-1" />
                                    Error Message
                                </h4>
                                <p className="text-sm text-red-700 font-mono whitespace-pre-wrap">{auditLog.errorMessage}</p>
                            </div>
                        </div>
                    )}
                </div>

                {auditLog.timestamp && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                        <div className="flex justify-between text-xs text-gray-500">
                            <span>Entry ID: {auditLog.id}</span>
                            <span>Logged at {format(new Date(auditLog.timestamp), "PPpp")}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
